/**
 * Real spawn-child routes.
 *
 * Phase 6 scaffold. The server-side route does not exist yet; this
 * file is wired so tests can stub responses today and the symbol swap
 * in `client.ts` is already in place.
 *
 *   POST /gi/{parent_root_id}/spawn
 *     Body: SparkDraft                (the child's manifest draft)
 *     → SpawnChildResponse
 *     422 Unprocessable Entity        (`depth-exceeded`)
 *     429 Too Many Requests           (`rate-limited`)
 *
 * Response shape echoes Spark — the server returns a fully-materialized
 * child record whose `lineage.parent_hash` links to the parent's
 * manifest hash and whose `lineage.depth` is `parent.depth + 1`.
 */

import type { EntityId, GiSummary, SparkDraft } from '../types';
import { ApiHttpError, apiFetch } from '../http';

export interface SpawnChildResponse {
  /** The child's newly minted root_id. */
  root_id: EntityId;
  /** Parent manifest hash — `sha256:…`. */
  parent_hash: string;
  /** Lineage depth of the *child* (0-indexed). Always `parent.depth + 1`. */
  depth: number;
  /** Full child summary — same shape the Vault lists render. */
  summary: GiSummary;
}

/** Classifies a failure from `spawnChildGi` so the caller can pick the
 *  right error message without parsing `detail` strings. */
export type SpawnError =
  | { kind: 'depth-exceeded'; limit: number; detail: string }
  | { kind: 'rate-limited'; retry_after_ms: number | null; detail: string }
  | { kind: 'http'; status: number; detail: string };

export async function spawnChildGi(
  parent_root_id: EntityId,
  draft: SparkDraft,
): Promise<SpawnChildResponse> {
  return await apiFetch<SpawnChildResponse>(
    `/gi/${encodeURIComponent(parent_root_id)}/spawn`,
    { method: 'POST', body: draft },
  );
}

/** Convert an `ApiHttpError` from `spawnChildGi` into a typed
 *  `SpawnError`. Non-`ApiHttpError` errors are returned as
 *  `{ kind: 'http', status: 0 }` so call sites can still branch on
 *  `kind` without a second `instanceof` guard. */
export function classifySpawnError(err: unknown): SpawnError {
  if (!(err instanceof ApiHttpError)) {
    return { kind: 'http', status: 0, detail: (err as Error)?.message ?? String(err) };
  }
  if (err.status === 422 && (err.code === 'depth-exceeded' || /depth/i.test(err.title))) {
    const match = /(\d+)/.exec(err.detail ?? err.title);
    const limit = match ? parseInt(match[1]!, 10) : 9;
    return { kind: 'depth-exceeded', limit, detail: err.detail ?? err.title };
  }
  if (err.status === 429) {
    return {
      kind: 'rate-limited',
      retry_after_ms: null,
      detail: err.detail ?? err.title,
    };
  }
  return { kind: 'http', status: err.status, detail: err.detail ?? err.title };
}
